import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ENV } from '../../config/env';
import toast from 'react-hot-toast';
import { logger } from '../../utils/logger';
import { format, addDays } from 'date-fns';
// Using inline SVGs with Tailwind classes instead of React Icons

interface PlanInfo {
  id: string;
  name: string;
  price: number;
  displayPrice?: string;
  monthlyEquivalent?: string;
  currency: string;
  interval: string;
  savings?: string;
  features: string[];
  highlighted?: boolean;
}

// These feature lists are the same for both plans since they're the same tier
const features = [
  'Unlimited Business Ideas',
  'Advanced Market Research',
  'Marketing Plan Generator',
  'Business Plan Generator',
  'PDF Export',
  'Priority Support'
];

const plans: PlanInfo[] = [
  {
    id: 'monthly',
    name: 'Monthly',
    price: 19,
    displayPrice: '19',
    currency: '£',
    interval: 'month',
    features
  },
  {
    id: 'annual',
    name: 'Annual',
    price: 182.40, // £19 × 12 months with 20% discount
    displayPrice: '182.40',
    monthlyEquivalent: '15.20',
    currency: '£',
    interval: 'year',
    savings: '20%',
    highlighted: true,
    features
  }
];

const PaymentPage: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const { user, getToken } = useAuth();
  const navigate = useNavigate();
  const trialEndDate = format(addDays(new Date(), 7), 'MMMM do, yyyy');

  // Get query parameters
  const location = useLocation();
  
  // Initialize with plan from URL parameter, redirect if not logged in, and auto-checkout if needed
  useEffect(() => {
    // Check if user is logged in
    if (!user) {
      navigate('/login');
      return;
    }
    
    // Parse URL parameters for plan selection
    const queryParams = new URLSearchParams(location.search);
    const planParam = queryParams.get('plan');
    const autoCheckout = queryParams.get('autoCheckout') === 'true';
    
    // Check if the plan parameter exists and is valid
    if (planParam && plans.some(plan => plan.id === planParam)) {
      setSelectedPlan(planParam);
      logger.debug('Plan selected from URL parameter:', planParam);
      
      // If auto-checkout is specified, trigger the subscription process immediately
      if (autoCheckout) {
        // Use setTimeout to ensure state is updated before triggering checkout
        setTimeout(() => {
          logger.debug('Auto-triggering checkout for plan:', planParam);
          handleSubscribe(planParam);
        }, 500);
      }
    }
  }, [user, navigate, location.search]);

  const handleSelectPlan = (planId: string) => {
    setSelectedPlan(planId);
  };

  const handleSubscribe = async (planId?: string) => {
    // Use the provided planId or fall back to the selected plan from state
    const planToUse = planId || selectedPlan;
    
    if (!planToUse) {
      toast.error('Please select a plan');
      return;
    }

    if (!user) {
      toast.error('Please log in to subscribe');
      navigate('/login');
      return;
    }
    
    // Show confirmation screen instead of proceeding directly to Stripe
    if (!showConfirmation) {
      setShowConfirmation(true);
      return;
    }
    
    // Log checkout attempt in development only
    logger.debug('User attempting checkout:', {
      id: user.id,
      isLoggedIn: !!user,
      selectedPlan: planToUse
    });

    try {
      setLoading(true);
      
      // Get userId from the getToken function (which now returns userId)
      const userId = await getToken();
      
      // Validate inputs before making API call
      if (!userId) {
        throw new Error('Authentication error. Please try logging in again.');
      }

      logger.debug(`Making request to: ${ENV.API_URL}/api/payment/checkout`, { userId });
      
      // Call our backend to create a Stripe Checkout session
      const response = await fetch(`${ENV.API_URL}/api/payment/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          planId: planToUse,
          userId: userId
        })
      });

      logger.debug('Response received', { status: response.status });
      
      // Handle error responses
      if (!response.ok) {
        // Try to get the error details as text first to avoid JSON parse errors
        const errorText = await response.text();
        let errorMessage = 'Failed to create checkout session';
        
        try {
          // Try to parse as JSON if possible
          if (errorText.trim().startsWith('{')) {
            const errorData = JSON.parse(errorText);
            errorMessage = errorData.details || errorMessage;
          } else if (errorText.includes('<!DOCTYPE')) {
            // HTML response detected
            errorMessage = `Server returned HTML instead of JSON (Status: ${response.status}). Check server logs.`;
            logger.error('HTML error response:', errorText.substring(0, 200) + '...');
          } else {
            errorMessage = errorText || errorMessage;
          }
        } catch (e) {
          logger.error('Error parsing error response:', e);
        }
        
        throw new Error(errorMessage);
      }

      // Extract the checkout URL and redirect to Stripe
      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      logger.error('Stripe checkout error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to process payment');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return <div className="p-8 text-center">Loading...</div>;
  }

  // Render confirmation screen if showConfirmation is true
  if (showConfirmation) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-screen-xl">
        <div className="max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8 border-2 border-blue-500">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600">
              7-Day Free Trial Confirmation
            </h1>
            
            <div className="bg-blue-100 text-blue-800 font-semibold px-4 py-3 rounded-lg my-4">
              Your free trial will start today and run until {trialEndDate}
            </div>
            
            <div className="mt-6 space-y-4 text-left">
              <div className="flex items-start">
                <svg className="h-6 w-6 text-green-500 mr-2 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span className="text-lg">Full access to all premium features for 7 days</span>
              </div>
              <div className="flex items-start">
                <svg className="h-6 w-6 text-green-500 mr-2 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span className="text-lg"><strong>No charge</strong> until {trialEndDate}</span>
              </div>
              <div className="flex items-start">
                <svg className="h-6 w-6 text-green-500 mr-2 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span className="text-lg">Cancel anytime during the trial with no charge</span>
              </div>
              <div className="flex items-start">
                <svg className="h-6 w-6 text-amber-500 mr-2 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <span className="text-lg">After your trial ends on {trialEndDate}, your card will be automatically charged</span>
              </div>
            </div>
          </div>
          
          <div className="mt-8 text-center">
            <button
              onClick={() => handleSubscribe()}
              disabled={loading}
              className="py-4 px-10 rounded-xl font-bold text-lg transition-all bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 shadow-lg hover:shadow-xl transform hover:-translate-y-1 w-full mb-4"
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-6 w-6 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </>
              ) : (
                'Provide Payment Details to Start Your Trial'
              )}
            </button>
            
            <button
              onClick={() => setShowConfirmation(false)}
              className="text-blue-600 hover:text-blue-800 font-semibold"
            >
              Go back to plan selection
            </button>
            
            <p className="mt-6 text-center text-sm text-gray-600">
              By proceeding, I confirm that I understand that my card will be charged after the 7-day trial unless I cancel.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-12 max-w-7xl">
      <div className="text-center mb-16">
        <h1 className="text-4xl font-extrabold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
          Upgrade Your Explanatum Experience
        </h1>
        <p className="text-lg text-gray-700 max-w-3xl mx-auto">
          Choose the plan that works best for you and unlock the full potential of your business ideas.
        </p>
      </div>

      <div className="flex flex-col md:flex-row justify-center md:space-x-4 space-y-4 md:space-y-0 mb-8">
        {plans.map((plan) => (
          <div
            key={plan.id}
            className={`border rounded-lg p-6 w-full md:w-80 cursor-pointer transition-all duration-300 ${
              selectedPlan === plan.id
                ? 'border-blue-500 shadow-lg bg-blue-50'
                : 'border-gray-200 hover:border-blue-300 hover:shadow'
            }`}
            onClick={() => handleSelectPlan(plan.id)}
          >
            <div className="text-center">
              <h3 className="text-xl font-bold mb-2">{plan.name} Subscription</h3>
              
              {/* Price display */}
              <div className="text-3xl font-bold mb-1">
                {plan.currency}{plan.displayPrice || plan.price}
                <span className="text-sm text-gray-500 ml-1">/
                  {plan.interval === 'month' ? 'month' : 'year'}
                </span>
              </div>
              
              {/* Show monthly equivalent for annual plan */}
              {plan.id === 'annual' && plan.monthlyEquivalent && (
                <div className="text-sm text-gray-600 mb-2">
                  ({plan.currency}{plan.monthlyEquivalent} per month, billed annually)
                </div>
              )}
              
              {/* Show savings tag for annual plan */}
              {plan.savings && (
                <div className="bg-green-100 text-green-800 text-sm font-medium px-2.5 py-0.5 rounded mb-4 inline-block">
                  Save {plan.savings}
                </div>
              )}
              
              <ul className="text-left space-y-2 my-6">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-start">
                    <svg className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>

      <div className="text-center">
        <button
          onClick={() => handleSubscribe()}
          disabled={!selectedPlan || loading}
          className={`py-4 px-10 rounded-xl font-bold text-lg transition-all ${!selectedPlan 
            ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
            : 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 shadow-lg hover:shadow-xl transform hover:-translate-y-1'}`}
        >
          {loading ? (
            <>
              <svg className="animate-spin h-6 w-6 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Processing...
            </>
          ) : (
            'Start My 7-Day Free Trial'
          )}
        </button>
        
        <div className="mt-4 space-y-2">
          <p className="text-sm text-gray-800 font-medium">
            Start your 7-day free trial today!
          </p>
          <p className="text-sm text-gray-600">
            Your card will be charged automatically after the trial period unless you cancel before the trial ends.
          </p>
          <p className="text-sm text-gray-600">
            Secure payment processing by Stripe. Cancel anytime during your trial with no charge.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;
